import Button from "@material-ui/core/Button";
import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import AssignCategoriesDialog from "@saleor/components/AssignCategoryDialog";
import AssignCollectionDialog from "@saleor/components/AssignCollectionDialog";
import AssignLanguageDialog from "@saleor/components/AssignLanguageDialog";
import AssignProductDialog from "@saleor/components/AssignProductDialog";
import AssignVariantDialog from "@saleor/components/AssignVariantDialog";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA, PAGINATE_BY } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { commonMessages, sectionNames } from "@saleor/intl";
import { SearchCornermarks_search_edges_node } from "@saleor/searches/types/SearchCornermarks";
import useCategorySearch from "@saleor/searches/useCategorySearch";
import useCollectionSearch from "@saleor/searches/useCollectionSearch";
import useCornermarkSearch from "@saleor/searches/useCornermarkSearch";
import useLanguageSearch from "@saleor/searches/useLanguageSearch";
import useProductSearch from "@saleor/searches/useProductSearch";
import useProductVariantSearch from "@saleor/searches/useProductVariantSearch";
import { ReorderEvent } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { move } from "@saleor/utils/lists";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { categoryUrl } from "../../categories/urls";
import { collectionUrl } from "../../collections/urls";
import { decimal, joinDateTime, maybe } from "../../misc";
import { productUrl, productVariantEditUrl } from "../../products/urls";
import {
  CornerMarkTypeEnum,
  DiscountValueTypeEnum,
  SaleType,
  SaleTypeEnum
} from "../../types/globalTypes";
import SaleDetailsPage, {
  SaleDetailsPageFormData,
  SaleDetailsPageTab
} from "../components/SaleDetailsPage";
// import { sale } from "../fixtures";
import {
  TypedSaleCataloguesAdd,
  TypedSaleCataloguesRemove,
  TypedSaleDelete,
  TypedSaleProductUpdate,
  TypedSaleUpdate,
  TypedSaleVariantUpdate,
  useSaleLanguagesAddMutation,
  useSaleLanguagesRemoveMutation,
  useSaleReorderProducts,
  useSaleReorderVariants
} from "../mutations";
import { TypedSaleDetails } from "../queries";
import { SaleCataloguesAdd } from "../types/SaleCataloguesAdd";
import { SaleCataloguesRemove } from "../types/SaleCataloguesRemove";
import { SaleDelete } from "../types/SaleDelete";
import { SaleProductUpdate } from "../types/SaleProductUpdate";
import { SaleUpdate } from "../types/SaleUpdate";
import { SaleVariantUpdate } from "../types/SaleVariantUpdate";
import {
  saleListUrl,
  saleUrl,
  SaleUrlDialog,
  SaleUrlQueryParams
} from "../urls";

interface SaleDetailsProps {
  id: string;
  params: SaleUrlQueryParams;
}

function discountValueTypeEnum(type: SaleType): DiscountValueTypeEnum {
  return type.toString() === DiscountValueTypeEnum.FIXED
    ? DiscountValueTypeEnum.FIXED
    : type.toString() === DiscountValueTypeEnum.CUSTOM
    ? DiscountValueTypeEnum.CUSTOM
    : type.toString() === DiscountValueTypeEnum.DIRECT
    ? DiscountValueTypeEnum.DIRECT
    : DiscountValueTypeEnum.PERCENTAGE;
}

export const SaleDetails: React.FC<SaleDetailsProps> = ({ id, params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const shop = useShop();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const intl = useIntl();
  const {
    loadMore: loadMoreCategories,
    search: searchCategories,
    result: searchCategoriesOpts
  } = useCategorySearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreCollections,
    search: searchCollections,
    result: searchCollectionsOpts
  } = useCollectionSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreProducts,
    search: searchProducts,
    result: searchProductsOpts
  } = useProductSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreProductVariants,
    search: searchProductVariants,
    result: searchProductVariantsOpts
  } = useProductVariantSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreCornermarks,
    search: searchCornermark,
    result: searchCornermarkOpts
  } = useCornermarkSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      type: CornerMarkTypeEnum.NORMAL
    }
  });
  const paginationState = createPaginationState(PAGINATE_BY, params);
  const changeTab = (tab: SaleDetailsPageTab) => {
    reset();
    navigate(
      saleUrl(id, {
        activeTab: tab
      })
    );
  };
  const {
    loadMore: loadMoreLanguages,
    search: searchLanguages,
    result: searchLanguagesOpts
  } = useLanguageSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const [saleLanguagesAdd, saleLanguagesAddOpts] = useSaleLanguagesAddMutation({
    onCompleted: data => {
      if (data.saleLanguagesAdd?.errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Sale Assign to Language"
          })
        });
        reset();
        closeModal();
      }
    }
  });
  const [saleLanguagesRemove] = useSaleLanguagesRemoveMutation({
    onCompleted: data => {
      if (data.saleLanguagesRemove?.errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Sale Unassign to Language"
          })
        });
        reset();
      }
    }
  });
  const handleSaleDelete = (data: SaleDelete) => {
    if (data.saleDelete.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Removed sale"
        })
      });
      navigate(saleListUrl(), true);
    }
  };

  const handleSaleUpdate = (data: SaleUpdate) => {
    if (data.saleUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
    }
  };
  const handleSaleProcutUpdate = (data: SaleProductUpdate) => {
    if (data.saleProductUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
    }
  };
  const handleSaleVariantUpdate = (data: SaleVariantUpdate) => {
    if (data.saleVariantUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
    }
  };

  const [openModal, closeModal] = createDialogActionHandlers<
    SaleUrlDialog,
    SaleUrlQueryParams
  >(navigate, params => saleUrl(id, params), params);

  const handleCatalogueAdd = (data: SaleCataloguesAdd) => {
    if (data.saleCataloguesAdd.errors.length === 0) {
      closeModal();
    }
  };

  const handleCatalogueRemove = (data: SaleCataloguesRemove) => {
    if (data.saleCataloguesRemove.errors.length === 0) {
      closeModal();
      reset();
    }
  };

  const canOpenBulkActionDialog = maybe(() => params.ids.length > 0);

  return (
    <TypedSaleCataloguesRemove onCompleted={handleCatalogueRemove}>
      {(saleCataloguesRemove, saleCataloguesRemoveOpts) => (
        <TypedSaleCataloguesAdd onCompleted={handleCatalogueAdd}>
          {(saleCataloguesAdd, saleCataloguesAddOpts) => (
            <TypedSaleUpdate onCompleted={handleSaleUpdate}>
              {(saleUpdate, saleUpdateOpts) => (
                <TypedSaleProductUpdate onCompleted={handleSaleProcutUpdate}>
                  {(saleProductUpdate, saleProductUpdateOpts) => (
                    <TypedSaleVariantUpdate
                      onCompleted={handleSaleVariantUpdate}
                    >
                      {(saleVariantUpdate, saleVariantUpdateOpts) => (
                        <TypedSaleDelete onCompleted={handleSaleDelete}>
                          {(saleDelete, saleDeleteOpts) => (
                            <TypedSaleDetails
                              displayLoader
                              variables={{ id, ...paginationState }}
                            >
                              {({ data, loading, refetch }) => {
                                const [
                                  saleReorderVariants
                                ] = useSaleReorderVariants({
                                  onCompleted: data => {
                                    if (
                                      data.customReorderVariants.errors
                                        .length === 0
                                    ) {
                                      notify({
                                        status: "success",
                                        text: intl.formatMessage({
                                          defaultMessage:
                                            "Reorder Redemption Variant Success"
                                        })
                                      });
                                      refetch();
                                    }
                                  }
                                });
                                const [
                                  saleReorderProducts
                                ] = useSaleReorderProducts({
                                  onCompleted: data => {
                                    if (
                                      data.customReorderProducts.errors
                                        .length === 0
                                    ) {
                                      notify({
                                        status: "success",
                                        text: intl.formatMessage({
                                          defaultMessage:
                                            "Reorder Redemption Product Success"
                                        })
                                      });
                                      refetch();
                                    }
                                  }
                                });
                                const tabPageInfo =
                                  params.activeTab ===
                                  SaleDetailsPageTab.categories
                                    ? maybe(() => data.sale.categories.pageInfo)
                                    : params.activeTab ===
                                      SaleDetailsPageTab.collections
                                    ? maybe(
                                        () => data.sale.collections.pageInfo
                                      )
                                    : params.activeTab ===
                                      SaleDetailsPageTab.variants
                                    ? maybe(() => data.sale.variants.pageInfo)
                                    : maybe(
                                        () => data.sale.saleProducts.pageInfo
                                      );

                                const handleCategoriesUnassign = (
                                  ids: string[]
                                ) =>
                                  saleCataloguesRemove({
                                    variables: {
                                      ...paginationState,
                                      id,
                                      input: {
                                        categories: ids
                                      }
                                    }
                                  });

                                const handleCollectionsUnassign = (
                                  ids: string[]
                                ) =>
                                  saleCataloguesRemove({
                                    variables: {
                                      ...paginationState,
                                      id,
                                      input: {
                                        collections: ids
                                      }
                                    }
                                  });

                                const handleProductsUnassign = (
                                  ids: string[]
                                ) =>
                                  saleCataloguesRemove({
                                    variables: {
                                      ...paginationState,
                                      id,
                                      input: {
                                        products: ids
                                      }
                                    }
                                  });

                                const handleVariantsUnassign = (
                                  ids: string[]
                                ) =>
                                  saleCataloguesRemove({
                                    variables: {
                                      ...paginationState,
                                      id,
                                      input: {
                                        variants: ids
                                      }
                                    }
                                  });
                                const handleExcludeCategoriesUnassign = (
                                  ids: string[]
                                ) =>
                                  saleCataloguesRemove({
                                    variables: {
                                      ...paginationState,
                                      id,
                                      input: {
                                        categories: ids,
                                        exclude: true
                                      }
                                    }
                                  });

                                const handleExcludeCollectionsUnassign = (
                                  ids: string[]
                                ) =>
                                  saleCataloguesRemove({
                                    variables: {
                                      ...paginationState,
                                      id,
                                      input: {
                                        collections: ids,
                                        exclude: true
                                      }
                                    }
                                  });

                                const handleExcludeProductsUnassign = (
                                  ids: string[]
                                ) =>
                                  saleCataloguesRemove({
                                    variables: {
                                      ...paginationState,
                                      id,
                                      input: {
                                        products: ids,
                                        exclude: true
                                      }
                                    }
                                  });

                                const handleExcludeVariantsUnassign = (
                                  ids: string[]
                                ) =>
                                  saleCataloguesRemove({
                                    variables: {
                                      ...paginationState,
                                      id,
                                      input: {
                                        variants: ids,
                                        exclude: true
                                      }
                                    }
                                  });

                                const handleSaleProductsUnassign = (
                                  ids: string[]
                                ) =>
                                  saleCataloguesRemove({
                                    variables: {
                                      ...paginationState,
                                      id,
                                      input: {
                                        products: ids
                                      }
                                    }
                                  });

                                const handleSubmit = async (
                                  saleData: SaleDetailsPageFormData
                                ) => {
                                  const result = await saleUpdate({
                                    variables: {
                                      id,
                                      input: {
                                        endDate: saleData.hasEndDate
                                          ? joinDateTime(
                                              saleData.endDate,
                                              saleData.endTime
                                            )
                                          : null,
                                        name: saleData.name,
                                        startDate: joinDateTime(
                                          saleData.startDate,
                                          saleData.startTime
                                        ),
                                        type: discountValueTypeEnum(
                                          saleData.type
                                        ),
                                        rangeType:
                                          saleData.type === SaleType.CUSTOM
                                            ? SaleTypeEnum.SPECIFIC_PRODUCT
                                            : saleData.rangeType,
                                        prescriptionRequired:
                                          saleData.prescriptionRequired,
                                        value: decimal(saleData.value),
                                        cornerMark: saleData.cornerMark
                                          ? ((saleData.cornerMark as unknown) as SearchCornermarks_search_edges_node)
                                              .id
                                          : "",
                                        showSuperPrice: saleData.showSuperPrice,
                                        showOriginalPrice:
                                          saleData.showOriginalPrice,
                                        onlyBuyOne: saleData.onlyBuyOne,
                                        superName: saleData.superName,
                                        superPriceText: saleData.superPriceText,
                                        superPriceImage:
                                          saleData.superPriceImage,
                                        superPriceImageMb:
                                          saleData.superPriceImageMb
                                      }
                                    }
                                  });
                                  const result2 = await saleProductUpdate({
                                    variables: {
                                      saleId: id,
                                      products: saleData.products
                                    }
                                  });
                                  const result3 = await saleVariantUpdate({
                                    variables: {
                                      saleId: id,
                                      variants: saleData.variants
                                    }
                                  });
                                  refetch();
                                  return (
                                    result.data.saleUpdate.errors ||
                                    result2.data.saleProductUpdate.errors ||
                                    result3.data.saleVariantUpdate.errors
                                  );
                                };

                                const {
                                  loadNextPage,
                                  loadPreviousPage,
                                  pageInfo
                                } = paginate(
                                  tabPageInfo,
                                  paginationState,
                                  params
                                );
                                const handleVariantReorder = ({
                                  newIndex,
                                  oldIndex
                                }: ReorderEvent) =>
                                  saleReorderVariants({
                                    optimisticResponse: {
                                      customReorderVariants: {
                                        __typename: "CustomReorderVariants",
                                        sale: {
                                          ...data.sale,
                                          customVariants: move(
                                            data.sale.customVariants[oldIndex],
                                            data.sale.customVariants,
                                            (a, b) => a.id === b.id,
                                            newIndex
                                          )
                                        },
                                        errors: []
                                      }
                                    },
                                    variables: {
                                      saleId: id,
                                      move: {
                                        saleVariantId:
                                          data.sale.customVariants[oldIndex].id,
                                        sortOrder: newIndex - oldIndex
                                      }
                                    }
                                  });
                                const handleProductReorder = ({
                                  newIndex,
                                  oldIndex
                                }: ReorderEvent) =>
                                  saleReorderProducts({
                                    optimisticResponse: {
                                      customReorderProducts: {
                                        __typename: "CustomReorderProducts",
                                        sale: {
                                          ...data.sale,
                                          customProducts: move(
                                            data.sale.customProducts[oldIndex],
                                            data.sale.customProducts,
                                            (a, b) => a.id === b.id,
                                            newIndex
                                          )
                                        },
                                        errors: []
                                      }
                                    },
                                    variables: {
                                      saleId: id,
                                      move: {
                                        saleProductId:
                                          data.sale.customProducts[oldIndex].id,
                                        sortOrder: newIndex - oldIndex
                                      }
                                    }
                                  });
                                return (
                                  <>
                                    <WindowTitle
                                      title={intl.formatMessage(
                                        sectionNames.sales
                                      )}
                                    />
                                    <SaleDetailsPage
                                      defaultCurrency={maybe(
                                        () => shop.defaultCurrency
                                      )}
                                      onVariantReorder={handleVariantReorder}
                                      onProductReorder={handleProductReorder}
                                      sale={maybe(() => data.sale)}
                                      disabled={
                                        loading ||
                                        saleCataloguesRemoveOpts.loading
                                      }
                                      errors={
                                        saleUpdateOpts.data?.saleUpdate
                                          .errors ||
                                        saleProductUpdateOpts.data
                                          ?.saleProductUpdate.errors ||
                                        saleVariantUpdateOpts.data
                                          ?.saleVariantUpdate.errors ||
                                        []
                                      }
                                      fetchCornermarks={searchCornermark}
                                      fetchMoreCornermarks={{
                                        hasMore:
                                          searchCornermarkOpts.data?.search
                                            .pageInfo.hasNextPage,
                                        loading: searchCornermarkOpts.loading,
                                        onFetchMore: loadMoreCornermarks
                                      }}
                                      cornermarks={(
                                        searchCornermarkOpts.data?.search
                                          .edges || []
                                      ).map(edge => edge.node)}
                                      pageInfo={pageInfo}
                                      onNextPage={loadNextPage}
                                      onPreviousPage={loadPreviousPage}
                                      onLanguageAssign={() =>
                                        openModal("assign-language")
                                      }
                                      onLanguageUnAssign={languageId =>
                                        saleLanguagesRemove({
                                          variables: {
                                            id,
                                            languages: [languageId]
                                          }
                                        })
                                      }
                                      onCollectionUnassign={collectionId =>
                                        handleCollectionsUnassign([
                                          collectionId
                                        ])
                                      }
                                      onExcludeCollectionUnassign={collectionId =>
                                        handleExcludeCollectionsUnassign([
                                          collectionId
                                        ])
                                      }
                                      onCategoryUnassign={categoryId =>
                                        handleCategoriesUnassign([categoryId])
                                      }
                                      onExcludeCategoryUnassign={categoryId =>
                                        handleExcludeCategoriesUnassign([
                                          categoryId
                                        ])
                                      }
                                      onProductUnassign={productId =>
                                        handleProductsUnassign([productId])
                                      }
                                      onExcludeProductUnassign={productId =>
                                        handleExcludeProductsUnassign([
                                          productId
                                        ])
                                      }
                                      onVariantUnassign={variantId =>
                                        handleVariantsUnassign([variantId])
                                      }
                                      onExcludeVariantUnassign={variantId =>
                                        handleExcludeVariantsUnassign([
                                          variantId
                                        ])
                                      }
                                      onCategoryClick={id => () =>
                                        navigate(categoryUrl(id))}
                                      onCollectionClick={id => () =>
                                        navigate(collectionUrl(id))}
                                      onProductClick={id => () =>
                                        navigate(productUrl(id))}
                                      onSaleProductAssign={() =>
                                        openModal("assign-product")
                                      }
                                      onSaleProductUnassign={productId =>
                                        handleSaleProductsUnassign([productId])
                                      }
                                      onVariantAssign={() =>
                                        openModal("assign-variant")
                                      }
                                      onProductAssign={() =>
                                        openModal("assign-product")
                                      }
                                      onCategoryAssign={() =>
                                        openModal("assign-category")
                                      }
                                      onCollectionAssign={() =>
                                        openModal("assign-collection")
                                      }
                                      onExcludeVariantAssign={() =>
                                        openModal("assign-exclude-variant")
                                      }
                                      onExcludeProductAssign={() =>
                                        openModal("assign-exclude-product")
                                      }
                                      onExcludeCategoryAssign={() =>
                                        openModal("assign-exclude-category")
                                      }
                                      onExcludeCollectionAssign={() =>
                                        openModal("assign-exclude-collection")
                                      }
                                      onVariantClick={(
                                        productId,
                                        variantId
                                      ) => () =>
                                        navigate(
                                          productVariantEditUrl(
                                            productId,
                                            variantId
                                          )
                                        )}
                                      activeTab={params.activeTab}
                                      onBack={() => navigate(saleListUrl())}
                                      onTabClick={changeTab}
                                      onSubmit={handleSubmit}
                                      onRemove={() => openModal("remove")}
                                      saveButtonBarState={
                                        saleUpdateOpts.status ||
                                        saleProductUpdateOpts.status
                                      }
                                      categoryListToolbar={
                                        <Button
                                          color="primary"
                                          onClick={() =>
                                            openModal("unassign-category", {
                                              ids: listElements
                                            })
                                          }
                                        >
                                          <FormattedMessage
                                            defaultMessage="Unassign"
                                            description="unassign category from sale, button"
                                            id="saleDetailsUnassignCategory"
                                          />
                                        </Button>
                                      }
                                      variantListToolbar={
                                        <Button
                                          color="primary"
                                          onClick={() =>
                                            openModal("unassign-variant", {
                                              ids: listElements
                                            })
                                          }
                                        >
                                          <FormattedMessage
                                            defaultMessage="Unassign"
                                            description="unassign product sku from sale, button"
                                            id="saleDetailsUnassignVariant"
                                          />
                                        </Button>
                                      }
                                      collectionListToolbar={
                                        <Button
                                          color="primary"
                                          onClick={() =>
                                            openModal("unassign-collection", {
                                              ids: listElements
                                            })
                                          }
                                        >
                                          <FormattedMessage
                                            defaultMessage="Unassign"
                                            description="unassign collection from sale, button"
                                            id="saleDetailsUnassignCollection"
                                          />
                                        </Button>
                                      }
                                      productListToolbar={
                                        <Button
                                          color="primary"
                                          onClick={() =>
                                            openModal("unassign-product", {
                                              ids: listElements
                                            })
                                          }
                                        >
                                          <FormattedMessage
                                            defaultMessage="Unassign"
                                            description="unassign product from sale, button"
                                            id="saleDetailsUnassignProduct"
                                          />
                                        </Button>
                                      }
                                      excludeCategoryListToolbar={
                                        <Button
                                          color="primary"
                                          onClick={() =>
                                            openModal(
                                              "unassign-exclude-category",
                                              {
                                                ids: listElements
                                              }
                                            )
                                          }
                                        >
                                          <FormattedMessage
                                            defaultMessage="Unassign"
                                            description="unassign category from sale, button"
                                            id="saleDetailsUnassignCategory"
                                          />
                                        </Button>
                                      }
                                      excludeVariantListToolbar={
                                        <Button
                                          color="primary"
                                          onClick={() =>
                                            openModal(
                                              "unassign-exclude-variant",
                                              {
                                                ids: listElements
                                              }
                                            )
                                          }
                                        >
                                          <FormattedMessage
                                            defaultMessage="Unassign"
                                            description="unassign product sku from sale, button"
                                            id="saleDetailsUnassignVariant"
                                          />
                                        </Button>
                                      }
                                      excludeCollectionListToolbar={
                                        <Button
                                          color="primary"
                                          onClick={() =>
                                            openModal(
                                              "unassign-exclude-collection",
                                              {
                                                ids: listElements
                                              }
                                            )
                                          }
                                        >
                                          <FormattedMessage
                                            defaultMessage="Unassign"
                                            description="unassign collection from sale, button"
                                            id="saleDetailsUnassignCollection"
                                          />
                                        </Button>
                                      }
                                      excludeProductListToolbar={
                                        <Button
                                          color="primary"
                                          onClick={() =>
                                            openModal(
                                              "unassign-exclude-product",
                                              {
                                                ids: listElements
                                              }
                                            )
                                          }
                                        >
                                          <FormattedMessage
                                            defaultMessage="Unassign"
                                            description="unassign product from sale, button"
                                            id="saleDetailsUnassignProduct"
                                          />
                                        </Button>
                                      }
                                      isChecked={isSelected}
                                      selected={listElements.length}
                                      toggle={toggle}
                                      toggleAll={toggleAll}
                                    />
                                    <AssignLanguageDialog
                                      languages={maybe(() =>
                                        searchLanguagesOpts.data.search.edges.map(
                                          edge => edge.node
                                        )
                                      )}
                                      confirmButtonState={
                                        saleLanguagesAddOpts.status
                                      }
                                      hasMore={
                                        searchLanguagesOpts.data?.search
                                          .pageInfo.hasNextPage
                                      }
                                      open={params.action === "assign-language"}
                                      onFetch={searchLanguages}
                                      onFetchMore={loadMoreLanguages}
                                      loading={searchLanguagesOpts.loading}
                                      onClose={closeModal}
                                      onSubmit={languages =>
                                        saleLanguagesAdd({
                                          variables: {
                                            ...paginationState,
                                            id,
                                            languages: languages.map(
                                              language => language.id
                                            )
                                          }
                                        })
                                      }
                                    />
                                    <AssignVariantDialog
                                      confirmButtonState={
                                        saleCataloguesAddOpts.status
                                      }
                                      hasMore={
                                        searchProductVariantsOpts.data?.search
                                          ?.pageInfo.hasNextPage
                                      }
                                      open={params.action === "assign-variant"}
                                      onFetch={searchProductVariants}
                                      onFetchMore={loadMoreProductVariants}
                                      loading={
                                        searchProductVariantsOpts.loading
                                      }
                                      onClose={closeModal}
                                      onSubmit={variants =>
                                        saleCataloguesAdd({
                                          variables: {
                                            ...paginationState,
                                            id,
                                            input: {
                                              variants: variants.map(
                                                variant => variant.id
                                              )
                                            }
                                          }
                                        })
                                      }
                                      variants={mapEdgesToItems(
                                        searchProductVariantsOpts?.data?.search
                                      )?.filter(
                                        suggestedVariant => suggestedVariant.id
                                      )}
                                    />
                                    <AssignVariantDialog
                                      confirmButtonState={
                                        saleCataloguesAddOpts.status
                                      }
                                      hasMore={
                                        searchProductVariantsOpts.data?.search
                                          ?.pageInfo.hasNextPage
                                      }
                                      open={
                                        params.action ===
                                        "assign-exclude-variant"
                                      }
                                      onFetch={searchProductVariants}
                                      onFetchMore={loadMoreProductVariants}
                                      loading={
                                        searchProductVariantsOpts.loading
                                      }
                                      onClose={closeModal}
                                      onSubmit={variants =>
                                        saleCataloguesAdd({
                                          variables: {
                                            ...paginationState,
                                            id,
                                            input: {
                                              variants: variants.map(
                                                variant => variant.id
                                              ),
                                              exclude: true
                                            }
                                          }
                                        })
                                      }
                                      variants={mapEdgesToItems(
                                        searchProductVariantsOpts?.data?.search
                                      )?.filter(
                                        suggestedVariant => suggestedVariant.id
                                      )}
                                    />
                                    <AssignProductDialog
                                      confirmButtonState={
                                        saleCataloguesAddOpts.status
                                      }
                                      hasMore={
                                        searchProductsOpts.data?.search
                                          ?.pageInfo.hasNextPage
                                      }
                                      open={params.action === "assign-product"}
                                      onFetch={searchProducts}
                                      onFetchMore={loadMoreProducts}
                                      loading={searchProductsOpts.loading}
                                      onClose={closeModal}
                                      onSubmit={products =>
                                        saleCataloguesAdd({
                                          variables: {
                                            ...paginationState,
                                            id,
                                            input: {
                                              products: products.map(
                                                product => product.id
                                              )
                                            }
                                          }
                                        })
                                      }
                                      products={mapEdgesToItems(
                                        searchProductsOpts?.data?.search
                                      )?.filter(
                                        suggestedProduct => suggestedProduct.id
                                      )}
                                    />
                                    <AssignProductDialog
                                      confirmButtonState={
                                        saleCataloguesAddOpts.status
                                      }
                                      hasMore={
                                        searchProductsOpts.data?.search
                                          ?.pageInfo.hasNextPage
                                      }
                                      open={
                                        params.action ===
                                        "assign-exclude-product"
                                      }
                                      onFetch={searchProducts}
                                      onFetchMore={loadMoreProducts}
                                      loading={searchProductsOpts.loading}
                                      onClose={closeModal}
                                      onSubmit={products =>
                                        saleCataloguesAdd({
                                          variables: {
                                            ...paginationState,
                                            id,
                                            input: {
                                              products: products.map(
                                                product => product.id
                                              ),
                                              exclude: true
                                            }
                                          }
                                        })
                                      }
                                      products={mapEdgesToItems(
                                        searchProductsOpts?.data?.search
                                      )?.filter(
                                        suggestedProduct => suggestedProduct.id
                                      )}
                                    />
                                    <AssignCategoriesDialog
                                      categories={maybe(() =>
                                        searchCategoriesOpts.data.search.edges
                                          .map(edge => edge.node)
                                          .filter(
                                            suggestedCategory =>
                                              suggestedCategory.id
                                          )
                                      )}
                                      confirmButtonState={
                                        saleCataloguesAddOpts.status
                                      }
                                      hasMore={
                                        searchCategoriesOpts.data?.search
                                          .pageInfo.hasNextPage
                                      }
                                      open={params.action === "assign-category"}
                                      onFetch={searchCategories}
                                      onFetchMore={loadMoreCategories}
                                      loading={searchCategoriesOpts.loading}
                                      onClose={closeModal}
                                      onSubmit={categories =>
                                        saleCataloguesAdd({
                                          variables: {
                                            ...paginationState,
                                            id,
                                            input: {
                                              categories
                                            }
                                          }
                                        })
                                      }
                                    />
                                    <AssignCategoriesDialog
                                      categories={maybe(() =>
                                        searchCategoriesOpts.data.search.edges
                                          .map(edge => edge.node)
                                          .filter(
                                            suggestedCategory =>
                                              suggestedCategory.id
                                          )
                                      )}
                                      confirmButtonState={
                                        saleCataloguesAddOpts.status
                                      }
                                      hasMore={
                                        searchCategoriesOpts.data?.search
                                          .pageInfo.hasNextPage
                                      }
                                      open={
                                        params.action ===
                                        "assign-exclude-category"
                                      }
                                      onFetch={searchCategories}
                                      onFetchMore={loadMoreCategories}
                                      loading={searchCategoriesOpts.loading}
                                      onClose={closeModal}
                                      onSubmit={categories =>
                                        saleCataloguesAdd({
                                          variables: {
                                            ...paginationState,
                                            id,
                                            input: {
                                              categories,
                                              exclude: true
                                            }
                                          }
                                        })
                                      }
                                    />
                                    <AssignCollectionDialog
                                      collections={maybe(() =>
                                        searchCollectionsOpts.data.search.edges
                                          .map(edge => edge.node)
                                          .filter(
                                            suggestedCategory =>
                                              suggestedCategory.id
                                          )
                                      )}
                                      confirmButtonState={
                                        saleCataloguesAddOpts.status
                                      }
                                      hasMore={
                                        searchCollectionsOpts.data?.search
                                          .pageInfo.hasNextPage
                                      }
                                      open={
                                        params.action === "assign-collection"
                                      }
                                      onFetch={searchCollections}
                                      onFetchMore={loadMoreCollections}
                                      loading={searchCollectionsOpts.loading}
                                      onClose={closeModal}
                                      onSubmit={collections =>
                                        saleCataloguesAdd({
                                          variables: {
                                            ...paginationState,
                                            id,
                                            input: {
                                              collections
                                            }
                                          }
                                        })
                                      }
                                    />
                                    <AssignCollectionDialog
                                      collections={maybe(() =>
                                        searchCollectionsOpts.data.search.edges
                                          .map(edge => edge.node)
                                          .filter(
                                            suggestedCategory =>
                                              suggestedCategory.id
                                          )
                                      )}
                                      confirmButtonState={
                                        saleCataloguesAddOpts.status
                                      }
                                      hasMore={
                                        searchCollectionsOpts.data?.search
                                          .pageInfo.hasNextPage
                                      }
                                      open={
                                        params.action ===
                                        "assign-exclude-collection"
                                      }
                                      onFetch={searchCollections}
                                      onFetchMore={loadMoreCollections}
                                      loading={searchCollectionsOpts.loading}
                                      onClose={closeModal}
                                      onSubmit={collections =>
                                        saleCataloguesAdd({
                                          variables: {
                                            ...paginationState,
                                            id,
                                            input: {
                                              collections,
                                              exclude: true
                                            }
                                          }
                                        })
                                      }
                                    />
                                    <ActionDialog
                                      open={
                                        params.action === "unassign-category" &&
                                        canOpenBulkActionDialog
                                      }
                                      title={intl.formatMessage({
                                        defaultMessage:
                                          "Unassign Categories From Sale",
                                        description: "dialog header"
                                      })}
                                      confirmButtonState={
                                        saleCataloguesRemoveOpts.status
                                      }
                                      onClose={closeModal}
                                      onConfirm={() =>
                                        handleCategoriesUnassign(params.ids)
                                      }
                                    >
                                      {canOpenBulkActionDialog && (
                                        <DialogContentText>
                                          <FormattedMessage
                                            defaultMessage="{counter,plural,one{Are you sure you want to unassign this category?} other{Are you sure you want to unassign {displayQuantity} categories?}}"
                                            description="dialog content"
                                            values={{
                                              counter: params.ids.length,
                                              displayQuantity: (
                                                <strong>
                                                  {params.ids.length}
                                                </strong>
                                              )
                                            }}
                                          />
                                        </DialogContentText>
                                      )}
                                    </ActionDialog>
                                    <ActionDialog
                                      open={
                                        params.action ===
                                          "unassign-collection" &&
                                        canOpenBulkActionDialog
                                      }
                                      title={intl.formatMessage({
                                        defaultMessage:
                                          "Unassign Collections From Sale",
                                        description: "dialog header"
                                      })}
                                      confirmButtonState={
                                        saleCataloguesRemoveOpts.status
                                      }
                                      onClose={closeModal}
                                      onConfirm={() =>
                                        handleCollectionsUnassign(params.ids)
                                      }
                                    >
                                      {canOpenBulkActionDialog && (
                                        <DialogContentText>
                                          <FormattedMessage
                                            defaultMessage="{counter,plural,one{Are you sure you want to unassign this collection?} other{Are you sure you want to unassign {displayQuantity} collections?}}"
                                            description="dialog content"
                                            values={{
                                              counter: params.ids.length,
                                              displayQuantity: (
                                                <strong>
                                                  {params.ids.length}
                                                </strong>
                                              )
                                            }}
                                          />
                                        </DialogContentText>
                                      )}
                                    </ActionDialog>
                                    <ActionDialog
                                      open={
                                        params.action === "unassign-product" &&
                                        canOpenBulkActionDialog
                                      }
                                      title={intl.formatMessage({
                                        defaultMessage:
                                          "Unassign Products From Sale",
                                        description: "dialog header"
                                      })}
                                      confirmButtonState={
                                        saleCataloguesRemoveOpts.status
                                      }
                                      onClose={closeModal}
                                      onConfirm={() =>
                                        handleProductsUnassign(params.ids)
                                      }
                                    >
                                      {canOpenBulkActionDialog && (
                                        <DialogContentText>
                                          <FormattedMessage
                                            defaultMessage="{counter,plural,one{Are you sure you want to unassign this product?} other{Are you sure you want to unassign {displayQuantity} products?}}"
                                            description="dialog content"
                                            values={{
                                              counter: params.ids.length,
                                              displayQuantity: (
                                                <strong>
                                                  {params.ids.length}
                                                </strong>
                                              )
                                            }}
                                          />
                                        </DialogContentText>
                                      )}
                                    </ActionDialog>
                                    <ActionDialog
                                      open={
                                        params.action === "unassign-variant" &&
                                        canOpenBulkActionDialog
                                      }
                                      title={intl.formatMessage({
                                        defaultMessage:
                                          "Unassign Variants From Sale",
                                        description: "dialog header"
                                      })}
                                      confirmButtonState={
                                        saleCataloguesRemoveOpts.status
                                      }
                                      onClose={closeModal}
                                      onConfirm={() =>
                                        handleVariantsUnassign(params.ids)
                                      }
                                    >
                                      {canOpenBulkActionDialog && (
                                        <DialogContentText>
                                          <FormattedMessage
                                            defaultMessage="{counter,plural,one{Are you sure you want to unassign this sku?} other{Are you sure you want to unassign {displayQuantity} sku?}}"
                                            description="dialog content"
                                            values={{
                                              counter: params.ids.length,
                                              displayQuantity: (
                                                <strong>
                                                  {params.ids.length}
                                                </strong>
                                              )
                                            }}
                                          />
                                        </DialogContentText>
                                      )}
                                    </ActionDialog>

                                    <ActionDialog
                                      open={
                                        params.action ===
                                          "unassign-exclude-category" &&
                                        canOpenBulkActionDialog
                                      }
                                      title={intl.formatMessage({
                                        defaultMessage:
                                          "Unassign Categories From Sale",
                                        description: "dialog header"
                                      })}
                                      confirmButtonState={
                                        saleCataloguesRemoveOpts.status
                                      }
                                      onClose={closeModal}
                                      onConfirm={() =>
                                        handleExcludeCategoriesUnassign(
                                          params.ids
                                        )
                                      }
                                    >
                                      {canOpenBulkActionDialog && (
                                        <DialogContentText>
                                          <FormattedMessage
                                            defaultMessage="{counter,plural,one{Are you sure you want to unassign this category?} other{Are you sure you want to unassign {displayQuantity} categories?}}"
                                            description="dialog content"
                                            values={{
                                              counter: params.ids.length,
                                              displayQuantity: (
                                                <strong>
                                                  {params.ids.length}
                                                </strong>
                                              )
                                            }}
                                          />
                                        </DialogContentText>
                                      )}
                                    </ActionDialog>
                                    <ActionDialog
                                      open={
                                        params.action ===
                                          "unassign-exclude-collection" &&
                                        canOpenBulkActionDialog
                                      }
                                      title={intl.formatMessage({
                                        defaultMessage:
                                          "Unassign Collections From Sale",
                                        description: "dialog header"
                                      })}
                                      confirmButtonState={
                                        saleCataloguesRemoveOpts.status
                                      }
                                      onClose={closeModal}
                                      onConfirm={() =>
                                        handleExcludeCollectionsUnassign(
                                          params.ids
                                        )
                                      }
                                    >
                                      {canOpenBulkActionDialog && (
                                        <DialogContentText>
                                          <FormattedMessage
                                            defaultMessage="{counter,plural,one{Are you sure you want to unassign this collection?} other{Are you sure you want to unassign {displayQuantity} collections?}}"
                                            description="dialog content"
                                            values={{
                                              counter: params.ids.length,
                                              displayQuantity: (
                                                <strong>
                                                  {params.ids.length}
                                                </strong>
                                              )
                                            }}
                                          />
                                        </DialogContentText>
                                      )}
                                    </ActionDialog>
                                    <ActionDialog
                                      open={
                                        params.action ===
                                          "unassign-exclude-product" &&
                                        canOpenBulkActionDialog
                                      }
                                      title={intl.formatMessage({
                                        defaultMessage:
                                          "Unassign Products From Sale",
                                        description: "dialog header"
                                      })}
                                      confirmButtonState={
                                        saleCataloguesRemoveOpts.status
                                      }
                                      onClose={closeModal}
                                      onConfirm={() =>
                                        handleExcludeProductsUnassign(
                                          params.ids
                                        )
                                      }
                                    >
                                      {canOpenBulkActionDialog && (
                                        <DialogContentText>
                                          <FormattedMessage
                                            defaultMessage="{counter,plural,one{Are you sure you want to unassign this product?} other{Are you sure you want to unassign {displayQuantity} products?}}"
                                            description="dialog content"
                                            values={{
                                              counter: params.ids.length,
                                              displayQuantity: (
                                                <strong>
                                                  {params.ids.length}
                                                </strong>
                                              )
                                            }}
                                          />
                                        </DialogContentText>
                                      )}
                                    </ActionDialog>
                                    <ActionDialog
                                      open={
                                        params.action ===
                                          "unassign-exclude-variant" &&
                                        canOpenBulkActionDialog
                                      }
                                      title={intl.formatMessage({
                                        defaultMessage:
                                          "Unassign Variants From Sale",
                                        description: "dialog header"
                                      })}
                                      confirmButtonState={
                                        saleCataloguesRemoveOpts.status
                                      }
                                      onClose={closeModal}
                                      onConfirm={() =>
                                        handleExcludeVariantsUnassign(
                                          params.ids
                                        )
                                      }
                                    >
                                      {canOpenBulkActionDialog && (
                                        <DialogContentText>
                                          <FormattedMessage
                                            defaultMessage="{counter,plural,one{Are you sure you want to unassign this sku?} other{Are you sure you want to unassign {displayQuantity} sku?}}"
                                            description="dialog content"
                                            values={{
                                              counter: params.ids.length,
                                              displayQuantity: (
                                                <strong>
                                                  {params.ids.length}
                                                </strong>
                                              )
                                            }}
                                          />
                                        </DialogContentText>
                                      )}
                                    </ActionDialog>

                                    <ActionDialog
                                      open={params.action === "remove"}
                                      title={intl.formatMessage({
                                        defaultMessage: "Delete Sale",
                                        description: "dialog header"
                                      })}
                                      confirmButtonState={saleDeleteOpts.status}
                                      onClose={closeModal}
                                      variant="delete"
                                      onConfirm={() =>
                                        saleDelete({
                                          variables: { id }
                                        })
                                      }
                                    >
                                      <DialogContentText>
                                        <FormattedMessage
                                          defaultMessage="Are you sure you want to delete {saleName}?"
                                          description="dialog content"
                                          values={{
                                            saleName: (
                                              <strong>
                                                {maybe(
                                                  () => data.sale.name,
                                                  "..."
                                                )}
                                              </strong>
                                            )
                                          }}
                                        />
                                      </DialogContentText>
                                    </ActionDialog>
                                  </>
                                );
                              }}
                            </TypedSaleDetails>
                          )}
                        </TypedSaleDelete>
                      )}
                    </TypedSaleVariantUpdate>
                  )}
                </TypedSaleProductUpdate>
              )}
            </TypedSaleUpdate>
          )}
        </TypedSaleCataloguesAdd>
      )}
    </TypedSaleCataloguesRemove>
  );
};
export default SaleDetails;
